import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import {
  DEVICE_HEADER,
  TwoStepMethod,
  TwoStepState,
} from "../features/twoStep/twoStep.utils";

/** Two-step sign-in API (backend `api/two-factor`, Roadmap 1845). */

export type TwoStepCheck = {
  state: TwoStepState;
  available: boolean;
  method?: TwoStepMethod | null;
  emailHint?: string | null;
  requiredBy: string[];
  proof?: string | null;
  proofExpiresAt?: string | null;
  deviceTokenInvalid?: boolean;
};

export type TwoStepPass = {
  proof: string;
  proofExpiresAt: string;
  deviceToken?: string | null;
  deviceExpiresAt?: string | null;
  recoveryCodesLeft: number;
  recoveryCodes?: string[] | null;
};

export type TwoStepSendCode = { sentTo: string; expiresInMinutes: number };

export type TwoStepAppSetup = {
  secret: string;
  otpAuthUrl: string;
  qrImage: string;
  account: string;
};

export type TwoStepSettings = {
  available: boolean;
  enabled: boolean;
  method?: TwoStepMethod | null;
  email?: string | null;
  requiredBy: string[];
  trustedDevices: number;
  recoveryCodesLeft: number;
  enabledAt?: string | null;
};

export type TwoStepFirm = {
  available: boolean;
  required: boolean;
  canManage: boolean;
  membersWithout: number;
};

export type TwoStepTeam = {
  available: boolean;
  canReset: boolean;
  members: { userId: string; enabled: boolean; method?: string | null }[];
};

export type TwoStepVerifyMethod = TwoStepMethod | "recovery";

export const twoStepSlice = createApi({
  reducerPath: "twoStepSlice",
  tagTypes: ["TwoStepSettings", "TwoStepFirm", "TwoStepTeam"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      headers.set("Authorization", getAuthHeader().Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    // ---- The two-step screen (open before the second step) ----
    checkTwoStep: build.mutation<TwoStepCheck, { deviceToken?: string | null }>(
      {
        query: ({ deviceToken }) => ({
          url: "api/two-factor/check",
          method: "POST",
          body: {},
          headers: deviceToken ? { [DEVICE_HEADER]: deviceToken } : undefined,
        }),
      },
    ),
    sendTwoStepCode: build.mutation<
      TwoStepSendCode,
      { purpose?: "signin" | "setup" | "disable" }
    >({
      query: (body) => ({
        url: "api/two-factor/send-code",
        method: "POST",
        body,
      }),
    }),
    verifyTwoStep: build.mutation<
      TwoStepPass,
      {
        code: string;
        method: TwoStepVerifyMethod;
        rememberDevice: boolean;
        deviceLabel?: string;
      }
    >({
      query: (body) => ({ url: "api/two-factor/verify", method: "POST", body }),
    }),
    startAppSetup: build.mutation<TwoStepAppSetup, void>({
      query: () => ({ url: "api/two-factor/setup/app", method: "POST" }),
    }),
    confirmAppSetup: build.mutation<TwoStepPass, { code: string }>({
      query: (body) => ({
        url: "api/two-factor/setup/app/confirm",
        method: "POST",
        body,
      }),
      // No invalidatesTags: the refetch must wait until the caller has saved
      // the new proof (TwoStepSetup.confirm), or it goes out without it and the
      // gate covers the recovery codes (QA 2026-10-21).
    }),
    startEmailSetup: build.mutation<TwoStepSendCode, void>({
      query: () => ({ url: "api/two-factor/setup/email", method: "POST" }),
    }),
    confirmEmailSetup: build.mutation<TwoStepPass, { code: string }>({
      query: (body) => ({
        url: "api/two-factor/setup/email/confirm",
        method: "POST",
        body,
      }),
      // No invalidatesTags: the refetch must wait until the caller has saved
      // the new proof (TwoStepSetup.confirm), or it goes out without it and the
      // gate covers the recovery codes (QA 2026-10-21).
    }),

    // ---- Profile ----
    getTwoStepSettings: build.query<TwoStepSettings, void>({
      query: () => ({ url: "api/two-factor/settings" }),
      providesTags: ["TwoStepSettings"],
    }),
    disableTwoStep: build.mutation<
      TwoStepSettings,
      { code: string; method: TwoStepVerifyMethod }
    >({
      query: (body) => ({
        url: "api/two-factor/disable",
        method: "POST",
        body,
      }),
      invalidatesTags: ["TwoStepSettings", "TwoStepFirm", "TwoStepTeam"],
    }),
    newRecoveryCodes: build.mutation<TwoStepPass, void>({
      query: () => ({ url: "api/two-factor/recovery-codes", method: "POST" }),
      invalidatesTags: ["TwoStepSettings"],
    }),
    forgetTwoStepDevices: build.mutation<TwoStepSettings, void>({
      query: () => ({
        url: "api/two-factor/devices/forget-all",
        method: "POST",
      }),
      invalidatesTags: ["TwoStepSettings"],
    }),

    // ---- Firm switch (Settings > Firm details) ----
    getTwoStepFirm: build.query<TwoStepFirm, { orgId: string }>({
      query: ({ orgId }) => ({
        url: "api/two-factor/firm",
        params: { orgId },
      }),
      providesTags: ["TwoStepFirm"],
    }),
    setTwoStepFirm: build.mutation<
      TwoStepFirm,
      { orgId: string; required: boolean }
    >({
      query: (body) => ({ url: "api/two-factor/firm", method: "PUT", body }),
      invalidatesTags: ["TwoStepFirm", "TwoStepSettings"],
    }),

    // ---- Team (Settings > Team) ----
    getTwoStepTeam: build.query<TwoStepTeam, { orgId: string }>({
      query: ({ orgId }) => ({
        url: "api/two-factor/team",
        params: { orgId },
      }),
      providesTags: ["TwoStepTeam"],
    }),
    resetTwoStep: build.mutation<
      TwoStepTeam,
      { orgId: string; userId: string }
    >({
      query: (body) => ({
        url: "api/two-factor/team/reset",
        method: "POST",
        body,
      }),
      invalidatesTags: ["TwoStepTeam", "TwoStepFirm"],
    }),
  }),
});

export const {
  useCheckTwoStepMutation,
  useSendTwoStepCodeMutation,
  useVerifyTwoStepMutation,
  useStartAppSetupMutation,
  useConfirmAppSetupMutation,
  useStartEmailSetupMutation,
  useConfirmEmailSetupMutation,
  useGetTwoStepSettingsQuery,
  useDisableTwoStepMutation,
  useNewRecoveryCodesMutation,
  useForgetTwoStepDevicesMutation,
  useGetTwoStepFirmQuery,
  useSetTwoStepFirmMutation,
  useGetTwoStepTeamQuery,
  useResetTwoStepMutation,
} = twoStepSlice;
